// Versão: 1.00
// Data..: 2026-09-24
// Página pública de resposta à pesquisa de satisfação (CSAT), acessada por token.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PublicShell } from './PublicShell.js';
import { publicApi, type PublicSurveyView } from '../../lib/publicApi.js';

export function PublicSurveyPage() {
  const { token } = useParams<{ token: string }>();
  const [survey, setSurvey] = useState<PublicSurveyView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    publicApi.getSurvey(token).then(setSurvey).catch(() => setError(true)).finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!token || rating < 1) return;
    setSending(true);
    try {
      await publicApi.answerSurvey(token, { rating, comment: comment || undefined });
      setDone(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const labels = ['', 'Muito insatisfeito', 'Insatisfeito', 'Neutro', 'Satisfeito', 'Muito satisfeito'];
  const alreadyAnswered = survey?.answered || done;

  return (
    <PublicShell>
      <div style={{ maxWidth: 560, margin: '48px auto 64px', padding: '0 24px' }}>
        {loading ? (
          <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>
        ) : error || !survey ? (
          <div className="card empty"><div className="empty-icon">🔍</div>Pesquisa não encontrada ou link inválido.</div>
        ) : alreadyAnswered ? (
          <div className="card fade-in" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44 }}>🙏</div>
            <h2>Obrigado pela sua avaliação!</h2>
            <p className="text-muted">Sua opinião ajuda a Makweb a melhorar o atendimento.</p>
            {(survey.rating || rating) ? <div style={{ fontSize: 28 }}>{'⭐'.repeat(survey.rating ?? rating)}</div> : null}
          </div>
        ) : (
          <div className="card fade-in">
            <h1 style={{ marginBottom: 4 }}>Como foi o atendimento?</h1>
            <p className="text-muted">
              Chamado {survey.ticketNumber ? `#${survey.ticketNumber}` : ''}{survey.subject ? ` — ${survey.subject}` : ''}
            </p>
            <div className="flex items-center justify-center gap-2" style={{ margin: '24px 0 8px' }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 40, lineHeight: 1, filter: (hover || rating) >= n ? 'none' : 'grayscale(1) opacity(0.4)', transition: 'transform 0.1s ease' }}
                  aria-label={`Nota ${n}`}
                >⭐</button>
              ))}
            </div>
            <div className="text-center text-muted text-sm" style={{ minHeight: 20 }}>{labels[hover || rating]}</div>
            <div className="field mt-4">
              <label className="label">Comentário (opcional)</label>
              <textarea className="textarea" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Conte o que achou do atendimento..." />
            </div>
            <button className="btn btn-primary btn-lg w-full" disabled={rating < 1 || sending} onClick={submit}>
              {sending ? 'Enviando...' : 'Enviar avaliação'}
            </button>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
